import { CustomerMetricsContext, getDeviceInfo } from '@moniebook-web/utils';
import dayjs from 'dayjs';
import { useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { mixpanelService } from '../../services';
import { useNetworkState } from '../use-network-state';
import type { UserEventApiData, UserEventData } from './type';

export function useCustomerAnalytics() {
    const location = useLocation();
    const { isOnline } = useNetworkState();
    const { pushData } = useContext(CustomerMetricsContext);

    const getEventData = useCallback(
        (eventData: UserEventData) => {
            const { eventName, properties = {} } = eventData;
            const deviceInfo = getDeviceInfo();

            properties.page_url = location.pathname;
            properties.deviceId = deviceInfo.deviceUniqueIdentifier?.toString();
            properties.deviceType = deviceInfo.userDeviceType;
            properties.os = deviceInfo.manufacturer;
            properties.browser = deviceInfo.browser.name;
            properties.version = deviceInfo.browser.version;
            properties.screen_resolution = deviceInfo.screenResolution;
            properties.isMobileDevice = deviceInfo.isMobileBrowser;
            properties.timestamp = dayjs().format('YYYY-MM-DDTHH:mm:ss');

            const apiData: UserEventApiData = {
                eventName,
                channel: 'WEB',
                properties,
            };

            return apiData;
        },
        [location.pathname],
    );

    const sendMixpanelEvent = useCallback(
        async (eventData: UserEventData) => {
            if (!eventData.eventName) return;

            const apiData = getEventData(eventData);

            if (!isOnline) {
                await mixpanelService.insert(apiData);
                return;
            }

            await pushData?.(apiData);
        },
        [pushData, getEventData, isOnline],
    );

    return {
        sendMixpanelEvent,
        /**
         * The central analytics service will filter out the events that can go
         * to Braze based on a whitelist.
         * This function exists for future extension
         */
        sendBrazeEvent: sendMixpanelEvent,
    };
}
